import { PropsWithChildren, useMemo, useState } from 'react'
import { ensureAbsoluteURL } from '@pogokid/util'
import {
  AuthProvider as OidcAuthProvider,
  useAuth,
} from 'react-oidc-context'
import {
  AuthContext,
  AuthContextProps,
  AuthProviderState,
} from './context.ts'
import { UserManager } from 'oidc-client-ts'
import { getAllLogger } from '@pogokid/log'

const log = getAllLogger('auth:AuthProvider')

interface AuthProviderProps {
  userManager: UserManager
}

function onSigninCallback() {
  console.log('signin callback triggered')
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname
  )
}

export function AuthProvider({
  children,
  userManager,
}: PropsWithChildren<AuthProviderProps>) {
  return (
    <OidcAuthProvider
      userManager={userManager}
      onSigninCallback={onSigninCallback}
    >
      <AuthProviderWithContext>
        {children}
      </AuthProviderWithContext>
    </OidcAuthProvider>
  )
}

function AuthProviderWithContext({
  children,
}: PropsWithChildren) {
  const auth = useAuth()

  const [state, setState] = useState<AuthProviderState>({
    error: null,
    isSyncingAuths: false,
    isInitialised: false,
    signUpUser: null,
  })

  const value = useMemo((): AuthContextProps => {
    return {
      ...state,
      isSyncingAuths: state.isSyncingAuths || auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      isConfirmed: !!auth.user,
      accessTokenState: auth.user?.expired
        ? 'expired'
        : auth.isAuthenticated
        ? 'active'
        : 'empty',
      accessToken: auth.user?.access_token,
      authUser: auth.user ?? null,

      async signIn(options) {
        if (state.isSyncingAuths) {
          return
        }
        try {
          setState((prev) => ({
            ...prev,
            error: null,
            isSyncingAuths: true,
            hasLinkSent: false,
          }))
          await auth.signinRedirect({
            prompt: options?.prompt,
            redirect_uri: options?.redirectUri
              ? ensureAbsoluteURL(
                  options.redirectUri,
                  window.location.origin
                )
              : undefined,
          })
        } catch (err: unknown) {
          log.error('Error signing in', err)
          setState((prev) => ({
            ...prev,
            error: err as Error,
            isSyncingAuths: false,
          }))
        }
      },

      async signOut({ returnTo } = {}): Promise<void> {
        return auth.signoutRedirect({
          post_logout_redirect_uri: returnTo,
        })
      },
    }
  }, [auth, state])
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
